import React, { useState, useEffect } from 'react';
import {
  Box,
  Stack,
  Autocomplete,
  TextField,
  Checkbox,
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { useEndReasonTypeData } from '../contexts';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

// Map of EndReasonTypeId to display names
const END_REASON_TYPE_OPTIONS = [
  { id: 1, name: 'Drop Out' },
  { id: 2, name: 'Withdrawal' },
  { id: 3, name: 'N/A' },
  { id: 4, name: 'Other' },
  { id: 5, name: 'Graduated' },
  { id: 6, name: 'Persisted Next Grade' },
  { id: 7, name: 'Persisted Same Grade' },
  { id: 8, name: 'Graduated With Rigorous' },
  { id: 9, name: 'Graduated Without Rigorous' },
  { id: 10, name: 'Deceased' },
  { id: 11, name: 'Unknown' },
];

interface YearOption {
  value: number;
  label: string;
}

interface TypeOption {
  id: number;
  name: string;
}

export const EndReasonTypeFilterControls = () => {
  const { filter, setFilter, activeSchoolYear, data } = useEndReasonTypeData();
  const [availableYears, setAvailableYears] = useState<YearOption[]>([]);

  // Get unique end reason types that exist in the data
  const availableTypes = React.useMemo(() => {
    const typeIds = new Set(data.map(d => d.endReasonTypeId));
    return END_REASON_TYPE_OPTIONS.filter(opt => typeIds.has(opt.id));
  }, [data]);

  useEffect(() => {
    if (activeSchoolYear) {
      const years = Array.from({ length: 10 }, (_, i) => activeSchoolYear - i);
      setAvailableYears(
        years.map(year => ({
          value: year,
          label: `${year}-${year + 1}`,
        }))
      );
    }
  }, [activeSchoolYear]);

  const handleYearChange = (_event: any, newValue: YearOption[]) => {
    setFilter({
      ...filter,
      schoolYears: newValue.map(option => option.value),
    });
  };

  const handleTypeChange = (_event: any, newValue: TypeOption[]) => {
    setFilter({
      ...filter,
      selectedEndReasonTypes: newValue.map(option => option.id),
    });
  };

  // Convert selected IDs back to option objects for Autocomplete
  const selectedYearOptions = availableYears.filter(year => 
    filter.schoolYears.includes(year.value)
  );

  const selectedTypeOptions = availableTypes.filter(type => 
    filter.selectedEndReasonTypes?.includes(type.id)
  );

  return (
    <Stack spacing={2} sx={{ mb: 2 }}>
      <Autocomplete
        multiple
        size="small"
        options={availableYears}
        value={selectedYearOptions}
        onChange={handleYearChange}
        disableCloseOnSelect
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        limitTags={2}
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              style={{ marginRight: 8 }}
              checked={selected}
            />
            {option.label}
          </li>
        )}
        renderInput={(params) => (
          <TextField {...params} label="School Year" placeholder="Select years" />
        )}
      />

      <Autocomplete
        multiple
        size="small"
        options={availableTypes}
        value={selectedTypeOptions}
        onChange={handleTypeChange}
        disableCloseOnSelect
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        limitTags={2}
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              style={{ marginRight: 8 }}
              checked={selected}
            />
            {option.name}
          </li>
        )}
        renderInput={(params) => (
          <TextField 
            {...params} 
            label="End Reason Type" 
            placeholder={selectedTypeOptions.length === 0 ? "All Types" : "Select types"} 
          />
        )}
      />
    </Stack>
  );
};